import { AccentIcon, AccentIconName } from '@oy/uiicon';
import classNames from 'classnames';
import React, { HTMLAttributes } from 'react';
import { Image, ImageNameType, Text } from '../../../Contents';
import { Padder, Spacer } from '../../../Layout';
import Button, { ButtonColor, ButtonType } from '../../Button/Button';
import Modal from '../BaseModal/BaseModal';
import styles from './ConfirmationModal.module.css';

export type ConfirmationModalProps = {
  children?: React.ReactNode;
  closeOnBackdropClick?: boolean;
  description?: string;
  icon?: AccentIconName;
  illustration?: ImageNameType;
  isPrimaryButtonDisabled?: boolean;
  isSecondaryButtonDisable?: boolean;
  onPrimaryButtonClick: () => void;
  onSecondaryButtonClick?: () => void;
  onClose?: () => void;
  primaryButtonColor?: ButtonColor;
  primaryButtonId?: string;
  primaryButtonName: string;
  secondaryButtonColor?: ButtonColor;
  secondaryButtonId?: string;
  secondaryButtonName?: string;
  showHeader?: boolean;
  showCloseButton?: boolean;
  show: boolean;
  title?: string;
  headerText?: string;
  typePrimaryButton?: 'submit' | 'reset' | 'button';
  typeSecondaryButton?: 'submit' | 'reset' | 'button';
  align?: 'center' | 'left' | 'right';
  primaryButtonType?: ButtonType;
  secondaryButtonType?: ButtonType;
  id?: string;
} & HTMLAttributes<HTMLDivElement>;

const ConfirmationModal = ({
  children,
  closeOnBackdropClick = false,
  description,
  icon,
  illustration,
  isPrimaryButtonDisabled = false,
  isSecondaryButtonDisable = false,
  onPrimaryButtonClick,
  onSecondaryButtonClick,
  onClose,
  primaryButtonColor = ButtonColor.Primary,
  primaryButtonId,
  primaryButtonName,
  primaryButtonType = ButtonType.Filled,
  secondaryButtonColor = ButtonColor.Primary,
  secondaryButtonId,
  secondaryButtonName,
  secondaryButtonType = ButtonType.Outlined,
  showHeader = false,
  showCloseButton = false,
  show,
  title,
  headerText,
  typePrimaryButton = 'button',
  typeSecondaryButton = 'button',
  align = 'left',
  id,
  ...props
}: ConfirmationModalProps) => {
  return (
    <Modal
      className={styles.container}
      show={show}
      closeOnBackdropClick={closeOnBackdropClick}
      onClose={onClose}
      id={id}
      {...props}
    >
      {showHeader && (
        <Modal.Header showCloseButton={showCloseButton}>
          {headerText}
        </Modal.Header>
      )}
      <Modal.Body>
        <Spacer
          bottom={Spacer.Size.L}
          className={classNames(styles.mainContainer, {
            [styles.textAlignCenter]: align === 'center',
            [styles.textAlignLeft]: align === 'left',
            [styles.textAlignRight]: align === 'right',
          })}
        >
          {illustration && (
            <Image name={illustration} className={styles.modalImage} />
          )}
          <div className={styles.contentContainer}>
            {icon && (
              <Padder
                left={Padder.Size.XS}
                right={Padder.Size.M}
                top={title ? Padder.Size.XS : Padder.Size.XXXXS}
              >
                <AccentIcon name={icon} size={title ? 'small-XL' : 'small-L'} />
              </Padder>
            )}
            <Padder>
              {title && (
                <Text textStyle={Text.Style.BodyXlSemibold}>{title}</Text>
              )}
              <Spacer top={Spacer.Size.XS} bottom={Spacer.Size.XS}>
                {description && (
                  <Text
                    style={{ whiteSpace: 'pre-line' }}
                    textStyle={Text.Style.BodyLgRegular}
                  >
                    {description}
                  </Text>
                )}
                {children}
              </Spacer>
            </Padder>
          </div>
        </Spacer>
        <div className={styles.buttonContainer}>
          {secondaryButtonName && onSecondaryButtonClick && (
            <Spacer className={styles.buttonWrapper} right={Spacer.Size.M}>
              <Button
                type={typeSecondaryButton}
                buttonType={secondaryButtonType}
                color={secondaryButtonColor}
                onClick={onSecondaryButtonClick}
                disabled={isSecondaryButtonDisable}
                id={secondaryButtonId}
                fullWidth
                isDedupe
              >
                {secondaryButtonName}
              </Button>
            </Spacer>
          )}
          <Button
            type={typePrimaryButton}
            buttonType={primaryButtonType}
            color={primaryButtonColor}
            onClick={onPrimaryButtonClick}
            disabled={isPrimaryButtonDisabled}
            id={primaryButtonId}
            fullWidth
            isDedupe
          >
            {primaryButtonName}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ConfirmationModal;
